<template>
  <va-layout
    :top="{ fixed: true, order: 2 }"
    :left="{ fixed: true, absolute: breakpoints.mdDown, order: 1, overlay: breakpoints.mdDown && !isSidebarMinimized }"
    @left-overlay-click="isSidebarMinimized = true"
  >
    <template #top>
      <app-navbar :is-mobile="isMobile" />
      <div style="position: absolute; top: 16px; right: 32px; z-index: 1000">
        <notification-dropdown />
      </div>
      <notification-toast />
    </template>

    <template #left>
      <app-sidebar :minimized="isSidebarMinimized" :animated="!isMobile" :mobile="isMobile" />
    </template>

    <template #content>
      <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <va-button class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <app-layout-navigation v-if="!isMobile" class="p-4" />
      <main class="p-4 pt-0">
        <article>
          <router-view />
        </article>
      </main>
    </template>
  </va-layout>

  <va-modal v-model="showModal" ok-text="Sim" cancel-text="Não" blur no-dismiss @ok="refresh" @cancel="logout">
    <h3 class="va-h3">Inatividade</h3>
    <p>Você ainda está aí?</p>
  </va-modal>

  <contract-modal :show-modal="showModalContract" @confirm="confirmContract" @cancel="logout" />

  <div v-if="GlobalStore.isLoading" class="global-loading-overlay">
    <va-progress-circle indeterminate size="large" color="primary" />
    <p class="mt-4 font-bold text-primary">Carregando contexto...</p>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onBeforeMount, onMounted, ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate, useRouter } from 'vue-router'
import { useBreakpoint, useToast } from 'vuestic-ui'

import { useGlobalStore } from '../stores/global-store'
import ContractModal from './ContractModal.vue'
import AppLayoutNavigation from '../components/app-layout-navigation/AppLayoutNavigation.vue'
import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'
import NotificationDropdown from '../components/notifications/NotificationDropdown.vue'
import NotificationToast from '../components/notifications/NotificationToast.vue'

import { authStore } from '../stores/auth'
import { useCounter, useIdle } from '@vueuse/core'
import { connectWebSocket, disconnectAllWebSockets } from '../services/websocketUtils'
import { EventBus } from '../services/eventBus'

const { init: notify } = useToast()

const GlobalStore = useGlobalStore()

const breakpoints = useBreakpoint()

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)

const isMobile = ref(false)
const isTablet = ref(false)
const { isSidebarMinimized } = storeToRefs(GlobalStore)

const onResize = () => {
  isSidebarMinimized.value = breakpoints.mdDown
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

const showModalContract = ref(true)

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()

  showModalContract.value = !auth.empEnabled
})

const showModal = ref(false)

EventBus.on('new-message', () => {
  // makeSound()
  notify({ message: 'Nova mensagem', color: 'success' })
})

const auth = authStore()

const checkToken = async () => {
  if (auth.token == null) {
    push({ name: 'login' })
  }
}

const refreshToken = async () => {
  setInterval(
    () => {
      refresh()
    },
    import.meta.env.VITE_SECONDS_TO_CHECK_TOKEN * 1000,
  )
}

const refresh = async () => {
  try {
    await auth.refresh_app_layout()
  } catch (error) {
    console.error(error)
  }
}

const confirmContract = async () => {
  try {
    const resp = await auth.confirmContract()
    if (resp.status == 200) {
      localStorage.setItem('empEnabled', JSON.stringify(true))
      auth.empEnabled = JSON.stringify(true)
    }
  } catch (error) {
    console.error(error)
  }
}

const { push } = useRouter()

const logout = async () => {
  disconnectAllWebSockets()
  auth.removeToken()
  push({ name: 'login' })
}

onBeforeMount(() => {
  checkToken()
  refreshToken()
  // Conecta WebSocket de notificações globalmente
  console.log('[AppLayout] Conectando WebSocket de notificações...')
  connectWebSocket(`ws/notifications/`, 'notifications')
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}

const { inc } = useCounter()

const { idle, reset } = useIdle(import.meta.env.VITE_SECONDS_TO_CHECK_IDLE * 60 * 1000)

watch(idle, (idleValue) => {
  if (idleValue) {
    inc()
    showModal.value = true
    reset()
  }
})
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}

.global-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}
</style>
